import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {Button} from 'primeng/button';
import {LanguageDropdownComponent} from '../../../../../shared/common/language-dropdown/language-dropdown.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Tooltip} from 'primeng/tooltip';
import {PlayerProfile} from '../../../../model/player-profile';
import {
  ChangeMailaddressComponent
} from '../../../../../backend/authentication/change-mailaddress/change-mailaddress.component';
import {ChangePasswordComponent} from '../../../../../backend/authentication/change-password/change-password.component';
import {DialogHelper} from '../../../../../shared/helper/dialog-helper';
import {DialogService} from 'primeng/dynamicdialog';
import {ImageChooserComponent} from '../../../../../../components/generic/image-chooser/image-chooser.component';
import {ConfirmationService} from 'primeng/api';
import {PlayerProfileService} from '../../../../service/player-profile.service';
import {RestErrorHelper} from '../../../../../../helper/rest-error-helper';
import {
  PlayerProfileSettingsDialogComponent
} from '../../../player-profile-settings-dialog/player-profile-settings-dialog.component';
import {
  PlayerProfilePaymentStatusComponent
} from './player-profile-payment-status/player-profile-payment-status.component';
import {Divider} from 'primeng/divider';
import {
  PlayerProfileFeaturesDialogComponent
} from '../../../player-profile-register/features/player-profile-features-dialog/player-profile-features-dialog.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {Password} from 'primeng/password';
import {FrontendNotificationService} from '../../../../../frontend/service/frontend-notification.service';
import {environment} from '../../../../../../../environments/environment';
import {FlutterService} from '../../../../../../services/app/flutter.service';
import {InfoTextComponent} from '../../../../../shared/common/info-text/info-text.component';

@Component({
  selector: 'app-player-profile-account-data',
  standalone: true,
  imports: [
    Button,
    LanguageDropdownComponent,
    TranslatePipe,
    Tooltip,
    PlayerProfilePaymentStatusComponent,
    Divider,
    ReactiveFormsModule,
    FormsModule,
    Password,
    InfoTextComponent
  ],
  templateUrl: './player-profile-account-data.component.html',
  styleUrl: './player-profile-account-data.component.scss'
})
export class PlayerProfileAccountDataComponent
{
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private confirmationService = inject(ConfirmationService);
  public playerProfileService = inject(PlayerProfileService);
  private restErrorHelper = inject(RestErrorHelper);
  private router = inject(Router);
  private frontendNotificationService = inject(FrontendNotificationService);
  public flutterService = inject(FlutterService);

  @Output() onReload = new EventEmitter<any>();
  @Output() onUpdatePlayerProfileValue = new EventEmitter<any>();

  @Input() playerProfile: PlayerProfile;

  system = this.frontendNotificationService.system;
  uploadingImage: boolean;
  showDeletePlayerProfileDetails: boolean;
  playerProfilePasswordDelete: string;
  isDeleting: boolean;
  showDebug = !environment.production;
  isLoggingOut: boolean;

  public changeMailAddress(): void
  {
    const ref = ChangeMailaddressComponent.showDialog(this.dialogService, this.translate, true);
    ref.onClose.subscribe((success) =>
    {
      if (success)
        setTimeout(() => this.onReload.emit());
    });
  }

  public changePassword(): void
  {
    this.dialogService.open(
      ChangePasswordComponent,
      DialogHelper.createBasicConfig(
        {
          header: this.translate.instant('CHANGE_PASSWORD'),
          width: DialogHelper.getDialogWidth(600),
          data: {playerProfile: true}
        })
    );
  }

  public selectPlayerImage(): void
  {
    if (this.uploadingImage)
      return;

    const data = {
      header: 'UPLOAD_IMAGE',
      maxWidth: 256
    };
    const ref = ImageChooserComponent.showDialog(this.dialogService, data, this.translate);
    ref.onClose.subscribe((result: any) =>
    {
      if (result?.success)
        this.uploadPlayerImage(result.imageFormData);
    });
  }

  private uploadPlayerImage(image: FormData): void
  {
    this.uploadingImage = true;
    this.playerProfileService.uploadProfileImage(image).subscribe({
      next: data =>
      {
        console.log('Bild hochgeladen', data);
        this.uploadingImage = false;
        this.onReload.emit();
      },
      error: error =>
      {
        this.uploadingImage = false;
        this.restErrorHelper.handleErrorService(error);
      }
    });
  }

  public deletePlayerImage(event: any): void
  {
    event.stopPropagation();
    event.preventDefault();

    if (this.uploadingImage)
      return;

    this.confirmationService.confirm({
      message: this.translate.instant('PLAYER_PROFILE_DELETE_IMAGE'),
      header: this.translate.instant('MSG_HEADER_DELETE_ENTRY'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      rejectButtonStyleClass: 'button-no button-right',
      accept: () =>
      {
        this.uploadingImage = true;
        this.playerProfileService.deleteProfileImage().subscribe({
          next: data =>
          {
            console.log('Bild gelöscht', data);
            this.uploadingImage = false;
            this.onReload.emit();
            // TODO Bilder laden
          },
          error: error =>
          {
            this.uploadingImage = false;
            this.restErrorHelper.handleErrorService(error);
          }
        });
      }
    });
  }

  public openSettings(): void
  {
    PlayerProfileSettingsDialogComponent.openDialog(this.dialogService, this.translate);
  }

  public openPlayerProfileFeatures(): void
  {
    PlayerProfileFeaturesDialogComponent.showDialog(this.dialogService, this.translate);
  }

  public logout(): void
  {
    this.isLoggingOut = true;
    this.playerProfileService.logout().subscribe({
      next: () =>
      {
        this.isLoggingOut = false;
        this.playerProfileService.playerAuthenticationService.routeToLogin(false);
      },
      error: error =>
      {
        this.isLoggingOut = false;
        this.restErrorHelper.handleErrorService(error);
        this.playerProfileService.playerAuthenticationService.logoutSuccess();
      }
    });
  }

  public updatePlayerProfileValue(body: any, inputField?: any): void
  {
    this.onUpdatePlayerProfileValue.emit({body, inputField});
  }

  public deletePlayerProfile(): void
  {
    if (!this.playerProfilePasswordDelete || this.isDeleting)
      return;

    this.isDeleting = true;
    this.playerProfileService.deletePlayerProfile({password: this.playerProfilePasswordDelete}).subscribe({
      next: data =>
      {
        console.log('Spielerprofil gelöscht', data);
        this.playerProfileService.playerAuthenticationService.logoutSuccess();
        this.router.navigate(['/frontend/player']).then(() => {});
        this.restErrorHelper.showInfoMessageService('PLAYER_PROFILE_DELETED_SUCCESSFULLY', 10000);
      },
      error: error =>
      {
        this.isDeleting = false;
        this.restErrorHelper.handleErrorService(error);
      }
    });
  }
}
